import {
  ArrowsPointingInIcon,
  ArrowsPointingOutIcon,
  PlusIcon,
} from '@heroicons/react/24/outline';
import { useEffect, useState } from 'react';

import { NotificationPermission } from '@/common/components/WidgetMenu/NotificationPermission';
import { UserMenu } from '@/common/components/WidgetMenu/UserMenu';
import { WidgetDrawer } from '@/common/components/WidgetMenu/WidgetDrawer';
import { useAuthStore } from '@/common/stores/useAuthStore';

interface WidgetMenuProps {
  activeWidgets: string[];
  onAddWidget: (widgetId: string) => void;
}

const WidgetMenu = ({ activeWidgets, onAddWidget }: WidgetMenuProps) => {
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(!!document.fullscreenElement);
  const { user } = useAuthStore();

  useEffect(() => {
    const handler = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener('fullscreenchange', handler);
    return () => document.removeEventListener('fullscreenchange', handler);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) {
      document.exitFullscreen();
    } else {
      document.documentElement.requestFullscreen();
    }
  };

  return (
    <>
      {/* Top Toolbar */}
      <div className="fixed top-0 left-0 px-8 right-0 h-12 bg-white border-b border-gray-100 z-40">
        <div className="h-full mx-auto flex items-center justify-between">
          {/* Left side - Logo and Title */}
          <div className="flex items-center gap-3">
            <img alt="Logo" className="h-6 w-auto" src="/logo.png" />
          </div>

          <div className="flex items-center gap-4">
            <button
              className="p-2 rounded-full hover:bg-gray-100 transition-all"
              title={isFullscreen ? 'Exit fullscreen' : 'Fullscreen'}
              onClick={toggleFullscreen}
            >
              {isFullscreen ? (
                <ArrowsPointingInIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
              ) : (
                <ArrowsPointingOutIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
              )}
            </button>
            <NotificationPermission />
            <button
              className="p-2 rounded-full hover:bg-gray-100 transition-all"
              onClick={() => setIsDrawerOpen(true)}
            >
              <PlusIcon className="h-5 w-5 text-gray-500 hover:text-gray-700" />
            </button>
            <UserMenu user={user} />
          </div>
        </div>
      </div>

      {/* Spacer div to offset content below the fixed header */}
      <div className="h-1" />

      <WidgetDrawer
        activeWidgets={activeWidgets}
        isOpen={isDrawerOpen}
        onAddWidget={onAddWidget}
        onClose={() => setIsDrawerOpen(false)}
      />
    </>
  );
};

export default WidgetMenu;
